import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Switch,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomTextField from "../../../../components/ui/Textfield";
import CustomSelect from "../../../../components/ui/Select";
import IconButton from "../../../../components/ui/IconButton";
import CompetencyQuestionEditor from "./CompetencyQuestionEditor";
import { getCompetencyTemplatesFromAPI } from "../../services/competencyApi";
import { DEPARTMENTS } from "../../constants";
import type { AssessmentSteps, CompetencyQuestion, CompetencyQuestionTemplate, JobPosition } from "../../types";

type JobFormData = Omit<JobPosition, "id" | "createdAt" | "active">;

interface NewJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddJob: (job: JobFormData) => void;
  job?: JobPosition | null;
  onUpdateJob?: (id: string, job: JobFormData) => void;
  onToggleActive?: (job: JobPosition) => void;
}

const JOB_TYPES: JobPosition["type"][] = ["Full-time", "Part-time", "Contract", "Remote"];
const WORK_MODES: JobPosition["workMode"][] = ["On-site", "Hybrid", "Remote"];

const ASSESSMENT_STEP_INFO: { key: keyof AssessmentSteps; title: string; description: string }[] = [
  {
    key: "candidateInfo",
    title: "Candidate Information",
    description: "Name, email, phone, LinkedIn, and resume upload.",
  },
  {
    key: "personality",
    title: "Personality Assessment",
    description: "16Personalities type selection and result file upload.",
  },
  {
    key: "competency",
    title: "Competency Test",
    description: "Timed scenario-based assessment with auto-scoring.",
  },
  {
    key: "videoPitch",
    title: "Video Pitch",
    description: "In-browser video recording or file upload (max 90 seconds).",
  },
];

const DEFAULT_ASSESSMENT_STEPS: AssessmentSteps = {
  candidateInfo: true,
  personality: true,
  competency: true,
  videoPitch: true,
};

const DEFAULT_STATE = {
  title: "",
  department: DEPARTMENTS[0],
  location: "",
  type: "Full-time" as JobPosition["type"],
  workMode: "On-site" as JobPosition["workMode"],
  salaryRange: "",
  description: "",
};

const toLines = (value: string) =>
  value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const NewJobModal: React.FC<NewJobModalProps> = ({ isOpen, onClose, onAddJob, job, onUpdateJob, onToggleActive }) => {
  const isEditMode = Boolean(job);

  const [title, setTitle] = useState(DEFAULT_STATE.title);
  const [department, setDepartment] = useState(DEFAULT_STATE.department);
  const [location, setLocation] = useState(DEFAULT_STATE.location);
  const [type, setType] = useState<JobPosition["type"]>(DEFAULT_STATE.type);
  const [workMode, setWorkMode] = useState<JobPosition["workMode"]>(DEFAULT_STATE.workMode);
  const [salaryRange, setSalaryRange] = useState(DEFAULT_STATE.salaryRange);
  const [description, setDescription] = useState(DEFAULT_STATE.description);
  const [responsibilitiesText, setResponsibilitiesText] = useState("");
  const [qualificationsText, setQualificationsText] = useState("");
  const [benefitsText, setBenefitsText] = useState("");
  const [assessmentSteps, setAssessmentSteps] = useState<AssessmentSteps>(DEFAULT_ASSESSMENT_STEPS);
  const [competencyQuestions, setCompetencyQuestions] = useState<CompetencyQuestion[]>([]);
  const [templates, setTemplates] = useState<CompetencyQuestionTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    void getCompetencyTemplatesFromAPI().then(setTemplates).catch(() => setTemplates([]));
    setSelectedTemplateId("");
    if (job) {
      setTitle(job.title);
      setDepartment(job.department);
      setLocation(job.location);
      setType(job.type);
      setWorkMode(job.workMode);
      setSalaryRange(job.salaryRange);
      setDescription(job.description);
      setResponsibilitiesText((job.responsibilities ?? []).join("\n"));
      setQualificationsText((job.qualifications ?? []).join("\n"));
      setBenefitsText((job.benefits ?? []).join("\n"));
      setAssessmentSteps(job.assessmentSteps ?? DEFAULT_ASSESSMENT_STEPS);
      setCompetencyQuestions(job.competencyQuestions ?? []);
    } else {
      setTitle(DEFAULT_STATE.title);
      setDepartment(DEFAULT_STATE.department);
      setLocation(DEFAULT_STATE.location);
      setType(DEFAULT_STATE.type);
      setWorkMode(DEFAULT_STATE.workMode);
      setSalaryRange(DEFAULT_STATE.salaryRange);
      setDescription(DEFAULT_STATE.description);
      setResponsibilitiesText("");
      setQualificationsText("");
      setBenefitsText("");
      setAssessmentSteps(DEFAULT_ASSESSMENT_STEPS);
      setCompetencyQuestions([]);
    }
  }, [isOpen, job]);

  const toggleAssessmentStep = (key: keyof AssessmentSteps) => {
    setAssessmentSteps((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const templatesForDepartment = templates.filter((t) => t.department === department);

  const handleLoadTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;
    setCompetencyQuestions(
      template.questions.map((q, i) => ({ ...q, id: `q-${Date.now()}-${i}` })),
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !department.trim() || !description.trim()) return;

    const jobData: JobFormData = {
      title,
      department,
      location,
      type,
      workMode,
      salaryRange,
      description,
      responsibilities: toLines(responsibilitiesText),
      qualifications: toLines(qualificationsText),
      benefits: toLines(benefitsText),
      assessmentSteps,
      competencyQuestions,
    };

    if (isEditMode && job && onUpdateJob) {
      onUpdateJob(job.id, jobData);
    } else {
      onAddJob(jobData);
    }

    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", pr: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={800}>
            {isEditMode ? "Edit Job Position" : "Create New Job Position"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Fill in the role details and select which assessment steps are required.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isEditMode && job && onToggleActive && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                {job.active ? "Active" : "Inactive"}
              </Typography>
              <Switch
                checked={job.active}
                onChange={() => onToggleActive(job)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#614438" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#614438" },
                }}
              />
            </Box>
          )}
          <IconButton onClick={onClose} size="small" tooltip="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <CustomTextField
            label="Job Title *"
            placeholder="e.g. Account Executive"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomSelect
                label="Department *"
                value={department}
                onChange={(e) => setDepartment(e.target.value as string)}
              >
                {DEPARTMENTS.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </CustomSelect>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextField
                label="Location"
                placeholder="e.g. Harare, Zimbabwe"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomSelect
                label="Employment Type"
                value={type}
                onChange={(e) => setType(e.target.value as JobPosition["type"])}
              >
                {JOB_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </CustomSelect>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomSelect
                label="Workplace Type"
                value={workMode}
                onChange={(e) => setWorkMode(e.target.value as JobPosition["workMode"])}
              >
                {WORK_MODES.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </CustomSelect>
            </Grid>
          </Grid>

          <CustomTextField
            label="Salary Range"
            placeholder="e.g. $35,000 – $50,000 per year"
            value={salaryRange}
            onChange={(e) => setSalaryRange(e.target.value)}
          />

          <CustomTextField
            label="Job Description *"
            placeholder="A brief summary of the role that candidates will see..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            required
          />

          <Box>
            <CustomTextField
              label="Key Responsibilities"
              placeholder="One responsibility per line..."
              value={responsibilitiesText}
              onChange={(e) => setResponsibilitiesText(e.target.value)}
              multiline
              rows={3}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Enter one responsibility per line.
            </Typography>
          </Box>

          <Box>
            <CustomTextField
              label="Qualifications"
              placeholder="One qualification per line..."
              value={qualificationsText}
              onChange={(e) => setQualificationsText(e.target.value)}
              multiline
              rows={3}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Enter one qualification per line.
            </Typography>
          </Box>

          <Box>
            <CustomTextField
              label="Benefits"
              placeholder="One benefit per line..."
              value={benefitsText}
              onChange={(e) => setBenefitsText(e.target.value)}
              multiline
              rows={3}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Enter one benefit per line.
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="body1" fontWeight={800}>
              Assessment Pipeline
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              Toggle which steps are required for this position.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {ASSESSMENT_STEP_INFO.map((step) => (
                <Box
                  key={step.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    borderRadius: "12px",
                    border: "1px solid rgba(0,0,0,0.12)",
                    p: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      {step.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {step.description}
                    </Typography>
                  </Box>
                  <Switch
                    checked={assessmentSteps[step.key]}
                    onChange={() => toggleAssessmentStep(step.key)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#614438" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#614438" },
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>

          {assessmentSteps.competency && (
            <>
              <Divider />
              <Box>
                <Typography variant="body1" fontWeight={800}>
                  Competency Questions
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                  Load a default set for this department, then customize as needed.
                </Typography>

                {templatesForDepartment.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                      Load from Template
                    </Typography>
                    <CustomSelect
                      label=""
                      size="small"
                      displayEmpty
                      value={selectedTemplateId}
                      onChange={(e) => handleLoadTemplate(e.target.value as string)}
                      renderValue={(value) =>
                        !value ? "Select a template..." : templatesForDepartment.find((t) => t.id === value)?.name ?? "Select a template..."
                      }
                    >
                      <MenuItem value="" disabled>
                        Select a template...
                      </MenuItem>
                      {templatesForDepartment.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.name}
                        </MenuItem>
                      ))}
                    </CustomSelect>
                  </Box>
                )}

                <CompetencyQuestionEditor questions={competencyQuestions} onChange={setCompetencyQuestions} />
              </Box>
            </>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 2 }}>
            <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 700 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "999px", boxShadow: "none", backgroundColor: "#614438", "&:hover": { backgroundColor: "#4d362c" } }}
            >
              {isEditMode ? "Save Changes" : "Create Job"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default NewJobModal;
